// Normalizes TRU customer fields into Meta's customer information parameters.
// Shared by the browser Pixel (advanced matching) and the Conversions API so both
// send identical values; the server hashes them, the Pixel hashes them itself.

export type MetaUserData = Partial<
  Record<"em" | "ph" | "fn" | "ln" | "ge" | "db" | "zp" | "ct" | "country" | "external_id", string>
>;

const COUNTRY_CALLING_CODES: Record<string, string> = { es: "34", pt: "351" };

const clean = (value: unknown) =>
  value === undefined || value === null ? "" : String(value).trim().toLowerCase();

// Drops accents and anything that isn't a letter, as Meta expects for names and cities.
const lettersOnly = (value: string) =>
  value.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z]/g, "");

// Phones are stored as "<prefix>-<number>"; Meta wants digits only, with the country code.
function normalizePhone(value: string, locale: string) {
  const digits = value.replace(/\D/g, "");
  if (!value.includes("-") && digits.length === 9 && COUNTRY_CALLING_CODES[locale]) {
    return COUNTRY_CALLING_CODES[locale] + digits;
  }
  return digits;
}

// Birthdays come as "yyyy-MM-dd" (sometimes with a time part); Meta wants YYYYMMDD.
function normalizeBirthday(value: string) {
  const date = value.slice(0, 10).replace(/-/g, "");
  return /^\d{8}$/.test(date) ? date : "";
}

export function normalizeMetaUserData(
  customer: Record<string, any> | null | undefined,
  locale: string
): MetaUserData {
  const c = customer ?? {};
  const gender = clean(c.customer_gender);

  const userData: MetaUserData = {
    em: clean(c.email),
    ph: normalizePhone(clean(c.customer_cell_phone), locale),
    fn: lettersOnly(clean(c.customer_name)),
    ln: lettersOnly(clean(c.customer_surname)),
    ge: gender === "m" || gender === "f" ? gender : "",
    db: normalizeBirthday(clean(c.customer_birthday)),
    zp: clean(c.customer_zip).replace(/[\s-]/g, ""),
    ct: lettersOnly(clean(c.customer_city)),
    country: COUNTRY_CALLING_CODES[locale] ? locale : "",
    external_id: clean(c.customer_id ?? c.id),
  };

  return Object.fromEntries(
    Object.entries(userData).filter(([, value]) => value)
  ) as MetaUserData;
}

// Builds the _fbc value from a stored "<click timestamp ms>.<fbclid>" pair.
export function fbcFromStoredClick(stored: string | null | undefined) {
  if (!stored) return undefined;
  const dot = stored.indexOf(".");
  const timestamp = stored.slice(0, dot);
  const fbclid = stored.slice(dot + 1);
  return dot > 0 && fbclid ? `fb.1.${timestamp}.${fbclid}` : undefined;
}

export const FBCLID_COOKIE = "meta_fbclid";
